import { AdmissionAggregateTotal, AdmissionMetric } from '@schooly/api';
import { TrendDownIcon, TrendUpIcon } from '@schooly/style';
import { ReactNode } from 'react';

export type TrendData = {
  displayText: string;
  tooltipKey?: string;
  backgroundColor?: string;
  icon?: ReactNode;
  color?: string;
};

export const formatNumber = (value: number): string => {
  return value.toFixed(1).replace('.', ',');
};

const formatPercentage = (value: number | null): string => {
  return value !== null ? `${formatNumber(value)}%` : '-';
};

export const calculateGenericTrend = (
  currentValue: number | null,
  comparedValue: number | null,
  calculateChange: (current: number, compared: number) => number,
  formatChange: (change: number) => string,
): TrendData => {
  if (currentValue === null && comparedValue === null) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-NoDataAvailableForEitherPeriod',
    };
  }

  if (currentValue === null) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-CurrentPeriodDataUnavailable',
    };
  }

  if (comparedValue === null) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-NoComparisonDataAvailable',
    };
  }

  if (currentValue === 0 && comparedValue === 0) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-NoActivityInBothPeriods',
    };
  }

  if (comparedValue === 0 && currentValue > 0) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-CannotCalculateChangeFromZeroBaseline',
      backgroundColor: 'success.superLight',
      color: 'success.main',
      icon: <TrendUpIcon className="reset-svg-currentColor" />,
    };
  }

  if (currentValue === 0 && comparedValue > 0) {
    return {
      displayText: 'N/A',
      tooltipKey: 'dashboard-statistics-ActivityDecreasedToZero',
      backgroundColor: 'error.superLight',
      color: 'error.main',
      icon: <TrendDownIcon className="reset-svg-currentColor" />,
    };
  }

  const change = calculateChange(currentValue, comparedValue);

  if (change > 0) {
    return {
      displayText: formatChange(change),
      backgroundColor: 'success.superLight',
      color: 'success.main',
      icon: <TrendUpIcon className="reset-svg-currentColor" />,
    };
  }

  if (change < 0) {
    return {
      displayText: formatChange(Math.abs(change)),
      backgroundColor: 'error.superLight',
      color: 'error.main',
      icon: <TrendDownIcon className="reset-svg-currentColor" />,
    };
  }

  return {
    displayText: formatChange(0),
  };
};

export const getMetricData = (total: AdmissionAggregateTotal[], metric: AdmissionMetric) => {
  return total.find((item) => item.name === metric);
};

const calculatePercentage = (target: number | null, source: number | null): number | null => {
  if (target === null || source === null || source === 0) {
    return null;
  }
  return (target / source) * 100;
};

type ConversionData = {
  currentPercentage: number | null;
  oldPercentage: number | null;
  displayPercentage: string;
  displayText: string;
  tooltipKey?: string;
  backgroundColor?: string;
  icon?: React.ReactNode;
  color?: string;
};

export const calculateConversionRate = (
  currentTarget: number | null,
  currentSource: number | null,
  previousTarget: number | null,
  previousSource: number | null,
): ConversionData => {
  const currentPercentage = calculatePercentage(currentTarget, currentSource);
  const oldPercentage = calculatePercentage(previousTarget, previousSource);

  const trendData = calculateGenericTrend(
    currentPercentage,
    oldPercentage,
    (current, old) => current - old,
    (change) => `${formatNumber(change)}pp`,
  );

  return {
    currentPercentage,
    oldPercentage,
    displayPercentage: formatPercentage(currentPercentage),
    displayText: trendData.displayText,
    tooltipKey: trendData.tooltipKey,
    backgroundColor: trendData.backgroundColor,
    icon: trendData.icon,
    color: trendData.color,
  };
};
